import { EditorialPageHero } from "@/components/sections/EditorialPageHero";
import { EditorialHeading } from "@/components/sections/EditorialHeading";
import { useParams, Navigate } from "@/lib/router-compat";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, MapPin, Target, TrendingUp, Users } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { getCiudadContenido, type CiudadContenido } from "@/data/seo-local-ciudades";

export function cityDeliverables(city: string) {
  return [
    {
      title: "Auditoría local y de competencia",
      freq: "Inicio",
      text: `Radiografía de tu ficha, tu web y las 10 fichas que hoy ocupan el mapa en ${city}, con las brechas concretas que hay que cerrar.`,
    },
    {
      title: "Google Business Profile completo",
      freq: "Inicio + continua",
      text: "Categorías principal y secundarias, servicios, productos, atributos, horarios especiales, fotos y publicaciones semanales.",
    },
    {
      title: "Páginas de zona y servicio",
      freq: "Mensual",
      text: `Contenido propio por barrio y servicio de ${city}, con entidades locales, enlazado interno y datos estructurados.`,
    },
    {
      title: "Programa de reseñas",
      freq: "Continua",
      text: "Sistema de captación de reseñas con mención de servicio y zona, más respuesta a todas las opiniones en menos de 24h.",
    },
    {
      title: "Citaciones y autoridad local",
      freq: "Mensual",
      text: `Alta y saneamiento de NAP en directorios sectoriales y de ${city}, más menciones en medios y entidades locales.`,
    },
    {
      title: "Informe de llamadas y solicitudes",
      freq: "Mensual",
      text: "Reporte con visibilidad en el mapa, acciones de la ficha, llamadas, rutas y formularios. Métricas de negocio, no de vanidad.",
    },
  ];
}

export function cityPhases(city: string) {
  return [
    {
      when: "Semana 1-2",
      title: "Diagnóstico y bloqueo de zona",
      text: `Auditamos la situación de partida, definimos el área real de captación dentro de ${city} y bloqueamos por contrato tu sector y tu zona.`,
    },
    {
      when: "Semana 2-4",
      title: "Cimientos técnicos y ficha",
      text: "Corrección técnica de la web (velocidad, indexación, datos estructurados) y reconstrucción completa de la ficha de Google Business Profile.",
    },
    {
      when: "Mes 2-3",
      title: "Contenido de zona y señales",
      text: `Publicación de páginas por barrio y servicio de ${city}, citaciones locales y arranque del programa de reseñas.`,
    },
    {
      when: "Mes 4 en adelante · Continuidad opcional",
      title: "Consolidación y expansión",
      text: "Tras el mínimo de 3 meses, si acordamos continuar, ampliamos a zonas colindantes, reforzamos autoridad con enlaces locales y optimizamos según los datos. Esta fase no prolonga el compromiso mínimo ni garantiza resultados.",
    },
  ];
}

export function intentWork(pattern: string) {
  const p = pattern.toLowerCase();
  if (p.includes("cerca") || p.includes("near") || p.includes("prop") || p.includes("preto"))
    return "Proximidad y prominencia: ficha activa, categorías exactas y contenido de barrio.";
  if (p.includes("abierto") || p.includes("obert") || p.includes("ahora") || p.includes("hoy") || p.includes("domingo"))
    return "Horarios y festivos siempre actualizados, atributos completos y actividad reciente en la ficha.";
  if (p.includes("urgen") || p.includes("24"))
    return "Página de servicio urgente, teléfono destacado y señales de disponibilidad inmediata.";
  if (p.includes("valorado") || p.includes("opinion") || p.includes("reseñ"))
    return "Volumen y frescura de reseñas, con mención de servicio y zona, y respuesta a todas.";
  if (p.includes("precio") || p.includes("presupuesto"))
    return "Contenido de precios orientativos y comparativas que resuelven la duda antes de la llamada.";
  if (p.includes("english") || p.includes("speaking"))
    return "Versión en inglés de ficha, contenido y respuestas para captar público internacional.";
  return "Contenido específico para esa intención, con enlazado interno y datos estructurados.";
}

export function buildCityFaqs(city: string, contenido: CiudadContenido | null) {
  return [
    ...(contenido?.faqs ?? []),
    {
      question: `¿Cuánto cuesta el SEO local en ${city}?`,
      answer: `No publicamos tarifas: cada empresa necesita una estrategia propia. La inversión depende del número de zonas de ${city} a cubrir, de la competencia del sector y del estado de partida de la web y la ficha. Alcance e inversión se concretan tras el diagnóstico, con compromiso mínimo de 3 meses. Este mínimo no garantiza resultados en ese plazo; la continuidad posterior se acuerda según la evaluación del proyecto.`,
    },
    {
      question: `¿En cuánto tiempo se ven resultados en ${city}?`,
      answer:
        "La evolución depende del estado inicial, la competencia, la elegibilidad de la ficha y las decisiones de Google. Definimos tareas y revisiones con datos disponibles, sin un plazo universal ni una posición garantizada. El mínimo de 3 meses no garantiza resultados; la continuidad posterior es opcional y a convenir.",
    },
    {
      question: `¿Qué significa la exclusividad por zona en ${city}?`,
      answer: `Que firmamos por contrato no trabajar con otro negocio de tu mismo sector en tu zona de ${city} mientras seas cliente. Por eso las plazas son limitadas y por eso preguntamos siempre el sector antes de aceptar un proyecto.`,
    },
    {
      question: "¿Trabajáis también la visibilidad en ChatGPT y Google AI Overviews?",
      answer: `Trabajamos una base SEO accesible y datos verificables del negocio. GEO · SEO 360 profundiza en la observación de menciones y fuentes en respuestas generativas; es distinto de Google Maps y no garantiza citación.`,
    },
  ];
}


interface CityData {
  name: string;
  province: string;
  description: string;
  keywords: string[];
}

export const citiesData: Record<string, CityData> = {
  'madrid': {
    name: 'Madrid',
    province: 'Madrid',
    description: 'la capital y centro neurálgico de España',
    keywords: ['SEO Local Madrid', 'posicionamiento Google Madrid', 'agencia SEO Madrid']
  },
  'barcelona': {
    name: 'Barcelona',
    province: 'Barcelona',
    description: 'la ciudad condal y hub tecnológico del Mediterráneo',
    keywords: ['SEO Local Barcelona', 'posicionamiento web Barcelona', 'marketing digital Barcelona']
  },
  'valencia': {
    name: 'Valencia',
    province: 'Valencia',
    description: 'la ciudad del Turia y tercer motor económico de España',
    keywords: ['SEO Local Valencia', 'posicionamiento Google Valencia', 'agencia SEO Valencia']
  },
  'sevilla': {
    name: 'Sevilla',
    province: 'Sevilla',
    description: 'la capital andaluza y puerta al sur de España',
    keywords: ['SEO Local Sevilla', 'posicionamiento web Sevilla', 'marketing digital Sevilla']
  },
  'bilbao': {
    name: 'Bilbao',
    province: 'Vizcaya',
    description: 'el corazón del País Vasco y centro empresarial del norte',
    keywords: ['SEO Local Bilbao', 'posicionamiento Google Bilbao', 'agencia SEO Bilbao']
  },
  'toledo': {
    name: 'Toledo',
    province: 'Toledo',
    description: 'la ciudad imperial, cuna de historia y cultura en Castilla-La Mancha',
    keywords: ['SEO Local Toledo', 'posicionamiento Google Toledo', 'agencia SEO Toledo']
  },
  'guadalajara': {
    name: 'Guadalajara',
    province: 'Guadalajara',
    description: 'una ciudad en auge del corredor del Henares con gran potencial empresarial',
    keywords: ['SEO Local Guadalajara', 'posicionamiento Google Guadalajara', 'agencia SEO Guadalajara']
  },
  'avila': {
    name: 'Ávila',
    province: 'Ávila',
    description: 'la ciudad amurallada Patrimonio de la Humanidad en Castilla y León',
    keywords: ['SEO Local Ávila', 'posicionamiento Google Ávila', 'agencia SEO Ávila']
  },
  'segovia': {
    name: 'Segovia',
    province: 'Segovia',
    description: 'una joya patrimonial con un tejido empresarial en crecimiento',
    keywords: ['SEO Local Segovia', 'posicionamiento Google Segovia', 'agencia SEO Segovia']
  },
  'cuenca': {
    name: 'Cuenca',
    province: 'Cuenca',
    description: 'la ciudad de las casas colgadas y un mercado local por conquistar',
    keywords: ['SEO Local Cuenca', 'posicionamiento Google Cuenca', 'agencia SEO Cuenca']
  },
  'granada': {
    name: 'Granada',
    province: 'Granada',
    description: 'la perla de Andalucía, referente turístico y universitario',
    keywords: ['SEO Local Granada', 'posicionamiento Google Granada', 'agencia SEO Granada']
  },
  'leon': {
    name: 'León',
    province: 'León',
    description: 'capital histórica del antiguo reino, centro de servicios del noroeste',
    keywords: ['SEO Local León', 'posicionamiento Google León', 'agencia SEO León']
  },
  'valladolid': {
    name: 'Valladolid',
    province: 'Valladolid',
    description: 'capital de Castilla y León y polo industrial del centro de España',
    keywords: ['SEO Local Valladolid', 'posicionamiento Google Valladolid', 'agencia SEO Valladolid']
  },
  'asturias': {
    name: 'Asturias',
    province: 'Asturias',
    description: 'el paraíso natural del norte de España con un mercado local vibrante',
    keywords: ['SEO Local Asturias', 'posicionamiento Google Asturias', 'agencia SEO Asturias']
  },
  'malaga': {
    name: 'Málaga',
    province: 'Málaga',
    description: 'la capital de la Costa del Sol y hub tecnológico del sur de Europa',
    keywords: ['SEO Local Málaga', 'posicionamiento Google Málaga', 'agencia SEO Málaga']
  },
  'zamora': {
    name: 'Zamora',
    province: 'Zamora',
    description: 'la ciudad del románico con un mercado local por liderar',
    keywords: ['SEO Local Zamora', 'posicionamiento Google Zamora', 'agencia SEO Zamora']
  },
  'lugo': {
    name: 'Lugo',
    province: 'Lugo',
    description: 'la ciudad amurallada de Galicia con oportunidades de posicionamiento únicas',
    keywords: ['SEO Local Lugo', 'posicionamiento Google Lugo', 'agencia SEO Lugo']
  },
  'murcia': {
    name: 'Murcia',
    province: 'Murcia',
    description: 'la huerta de Europa y un mercado digital en plena expansión',
    keywords: ['SEO Local Murcia', 'posicionamiento Google Murcia', 'agencia SEO Murcia']
  },
  'zaragoza': {
    name: 'Zaragoza',
    province: 'Zaragoza',
    description: 'la capital de Aragón y punto estratégico entre Madrid y Barcelona',
    keywords: ['SEO Local Zaragoza', 'posicionamiento Google Zaragoza', 'agencia SEO Zaragoza']
  },
  'alicante': {
    name: 'Alicante',
    province: 'Alicante',
    description: 'la puerta del Mediterráneo con un ecosistema empresarial en crecimiento',
    keywords: ['SEO Local Alicante', 'posicionamiento Google Alicante', 'agencia SEO Alicante']
  },
};

export default function SeoLocalCiudad() {
  const { ciudad } = useParams<{ ciudad: string }>();
  const ciudadLower = ciudad?.toLowerCase();
  const cityData = ciudadLower ? citiesData[ciudadLower] : null;
  const contenido = ciudadLower ? getCiudadContenido(ciudadLower) : null;

  if (!cityData) {
    return <Navigate to="/servicios/seo-local-ia" replace />;
  }

  // Normaliza la URL a minúsculas para evitar canibalización (/Barcelona vs /barcelona)
  if (ciudad !== ciudadLower) {
    return <Navigate to={`/servicios/seo-local/${ciudadLower}`} replace />;
  }

  const city = cityData.name;
  const faqs = buildCityFaqs(city, contenido);

  return (
    <div className="min-h-screen flex flex-col bg-seo-dark text-white">
      <Navbar />

      <main className="flex-grow pt-24">
        <div className="container mx-auto px-4 md:px-6">
          <Breadcrumbs
            items={[
              { label: "Servicios", path: "/mas-servicios" },
              { label: "SEO Local", path: "/servicios/seo-local-ia" },
              { label: city, path: `/servicios/seo-local/${ciudadLower}` },
            ]}
          />
        </div>

        <EditorialPageHero
          kicker={`SEO Local · ${city}`}
          eyebrow="Exclusividad por zona y sector"
          title={<>SEO local en {city}.</>}
          highlight="Visibilidad en Google Maps."
          lead={`SEO local para negocios con área real de servicio en ${city}.`}
          description={`Revisamos ficha, servicios, web y señales locales de negocios en ${city}. El diagnóstico define prioridades para Google Maps; las respuestas generativas se evalúan como un objetivo distinto, sin garantizar aparición.`}
          primaryCta={{ label: `Consultar disponibilidad en ${city}`, href: "/agenda-consultoria" }}
          secondaryCta={{ label: "Solicita un diagnóstico de tu empresa", href: "/agenda-consultoria" }}
          note="Propuesta personalizada tras diagnóstico · Plazas limitadas por zona y sector"
          video={false}
        />

        {/* 01 · Respuesta directa (GEO) */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <EditorialHeading
              kicker="Sección 01 · Respuesta directa"
              eyebrow={`${cityData.province}`}
              title={
                <>
                  Qué es el SEO local en <span className="text-gradient">{city}</span> y cómo se gana
                </>
              }
              lead={`Respuesta breve para quien está comparando agencias en ${city} ahora mismo.`}
            />
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7 rounded-2xl border border-premium-gold/25 bg-seo-card p-8">
                <p className="text-lg text-gray-200 leading-relaxed">
                  El SEO local en {city} consiste en conseguir que un negocio aparezca en el mapa de Google y
                  en los resultados orgánicos cuando alguien busca su servicio dentro de la ciudad o de su
                  zona de influencia. Se gana con tres bloques de trabajo: una ficha de Google Business
                  Profile completa y activa, una web técnicamente sólida con contenido específico de cada
                  zona y servicio, y señales de confianza constantes (reseñas, citaciones y menciones
                  locales). No hay atajos ni posiciones garantizadas: hay ejecución completa y sostenida.
                </p>
              </div>
              <div className="lg:col-span-5 rounded-2xl border border-white/10 bg-seo-card/60 p-8">
                <p className="text-[11px] uppercase tracking-[0.28em] text-premium-gold font-semibold mb-4">
                  Área de servicio
                </p>
                <dl className="space-y-5">
                  <div>
                    <dt className="text-3xl font-black text-seo-bright">{city}</dt>
                    <dd className="text-sm text-gray-400">{ciudadLower === "asturias" ? "Principado · núcleos y comarcas según tu área real" : "Ciudad y zonas de servicio acordadas en el diagnóstico"}</dd>
                  </div>
                  <div>
                    <dt className="text-3xl font-black text-seo-bright">1 cliente</dt>
                    <dd className="text-sm text-gray-400">Por sector y zona en {city}, por contrato</dd>
                  </div>
                  <div>
                    <dt className="text-3xl font-black text-seo-bright">3 meses</dt>
                    <dd className="text-sm text-gray-400">Compromiso mínimo, no plazo de resultados</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </section>

        {/* 02 · Contexto de mercado */}
        {contenido && (
          <section className="py-16 md:py-20 bg-seo-card/30">
            <div className="container mx-auto px-4 md:px-6">
              <EditorialHeading
                kicker="Sección 02 · Mercado"
                eyebrow="Análisis local"
                title={
                  <>
                    Cómo se compite de verdad en <span className="text-gradient">{city}</span>
                  </>
                }
                lead="Adaptamos la estrategia a tus servicios, tus clientes y las zonas donde trabajas."
              />
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl">
                {contenido.mercado.map((parrafo, i) => (
                  <p
                    key={i}
                    className="rounded-2xl border border-white/10 bg-seo-card p-7 text-gray-300 leading-relaxed"
                  >
                    {parrafo}
                  </p>
                ))}
              </div>

              <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-2xl border border-white/10 bg-seo-card p-7">
                  <h3 className="text-lg font-bold mb-4 flex items-center">
                    <MapPin className="w-5 h-5 text-premium-gold mr-2" /> Zonas donde trabajamos en {city}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {contenido.zonas.map((zona) => (
                      <span
                        key={zona}
                        className="rounded-full border border-border px-3 py-1.5 text-sm text-gray-400"
                      >
                        {zona}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-seo-card p-7">
                  <h3 className="text-lg font-bold mb-4 flex items-center">
                    <Target className="w-5 h-5 text-premium-gold mr-2" /> Sectores a analizar
                  </h3>
                  <ul className="space-y-2">
                    {contenido.sectores.map((sector) => (
                      <li key={sector} className="flex items-start text-gray-300">
                        <Check className="w-4 h-4 text-premium-gold mr-3 mt-1 flex-shrink-0" />
                        {sector}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 03 · Entregables */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <EditorialHeading
              kicker="Sección 03 · Alcance"
              eyebrow="Qué incluye el servicio"
              title={
                <>
                  Lo que ejecutamos cada mes en <span className="text-gradient">{city}</span>
                </>
              }
              lead="Trabajo concreto y verificable, con su frecuencia. Sin partidas vagas ni horas sin destino."
            />
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {cityDeliverables(city).map((item, i) => (
                <article
                  key={item.title}
                  className="rounded-2xl border border-white/10 bg-seo-card p-7 hover:border-premium-gold/40 transition-colors"
                >
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-[11px] uppercase tracking-[0.28em] text-premium-gold font-semibold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-white/45">{item.freq}</span>
                  </div>
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* 04 · Metodología */}
        <section className="py-16 md:py-20 bg-seo-card/30">
          <div className="container mx-auto px-4 md:px-6">
            <EditorialHeading
              kicker="Sección 04 · Proceso"
              eyebrow="Calendario de trabajo"
              title={
                <>
                  Cómo desplegamos el proyecto en <span className="text-gradient">{city}</span>
                </>
              }
              lead="Cuatro fases de trabajo orientativas, ajustadas en el diagnóstico; no fijan plazos de resultados."
            />
            <div className="mt-10 space-y-4 max-w-5xl">
              {cityPhases(city).map((phase, i) => (
                <div
                  key={phase.title}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 rounded-2xl border border-white/10 bg-seo-card p-7"
                >
                  <div className="md:col-span-3">
                    <span className="text-[11px] uppercase tracking-[0.28em] text-premium-gold font-semibold">
                      Fase {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-sm text-white/50 mt-1">{phase.when}</p>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="text-lg font-bold mb-2">{phase.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{phase.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 05 · Búsquedas */}
        {contenido && (
          <section className="py-16 md:py-20">
            <div className="container mx-auto px-4 md:px-6">
              <EditorialHeading
                kicker="Sección 05 · Búsquedas"
                eyebrow="Intención local"
                title={
                  <>
                    Ejemplos de consultas a analizar en <span className="text-gradient">{city}</span>
                  </>
                }
                lead="Seleccionamos las consultas relevantes para tu negocio a partir de estos ejemplos y de los datos del diagnóstico."
              />
              <div className="mt-10 max-w-4xl overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[520px]">
                  <thead>
                    <tr className="border-b border-premium-gold/30">
                      <th className="py-4 pr-4 text-[11px] uppercase tracking-[0.2em] text-white/50">
                        Patrón de búsqueda
                      </th>
                      <th className="py-4 text-[11px] uppercase tracking-[0.2em] text-white/50">
                        Qué revisar para esa intención
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {contenido.busquedas.map((b) => (
                      <tr key={b} className="border-b border-border">
                        <td className="py-4 pr-4 font-medium">
                          {b.includes(city) ? b : `${b} · ${city}`}
                        </td>
                        <td className="py-4 text-gray-400">{intentWork(b)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* 06 · Criterios de admisión */}
        <section className="py-16 md:py-20 bg-seo-card/30">
          <div className="container mx-auto px-4 md:px-6">
            <EditorialHeading
              kicker="Sección 06 · Filtro"
              eyebrow="No trabajamos con todo el mundo"
              title={<>Criterios de admisión</>}
              lead={`Solo aceptamos un negocio por sector y zona en ${city}. Antes de reservar la plaza, comprobamos que el proyecto tiene recorrido.`}
            />
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-premium-gold/25 bg-seo-card p-8">
                <h3 className="text-lg font-bold mb-5 flex items-center">
                  <TrendingUp className="w-5 h-5 text-premium-gold mr-2" /> Es para ti si…
                </h3>
                <ul className="space-y-3">
                  {[
                    `Tienes un negocio con ubicación física o área de servicio real en ${city}`,
                    "Puedes sostener un proyecto continuado durante al menos 3 meses",
                    "Entiendes que el SEO local se mide en llamadas y clientes, no en posiciones sueltas",
                    "Quieres bloquear tu zona y tu sector para que no trabajemos con tu competencia",
                  ].map((t) => (
                    <li key={t} className="flex items-start text-gray-300">
                      <Check className="w-4 h-4 text-premium-gold mr-3 mt-1 flex-shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-white/10 bg-seo-card p-8">
                <h3 className="text-lg font-bold mb-5 flex items-center">
                  <Users className="w-5 h-5 text-white/50 mr-2" /> No es para ti si…
                </h3>
                <ul className="space-y-3">
                  {[
                    "Buscas la agencia más barata de la ciudad",
                    "Esperas resultados en dos semanas o una posición garantizada",
                    "No puedes atender el aumento de llamadas y solicitudes",
                    "Tu plaza en tu sector y zona ya está ocupada por otro cliente",
                  ].map((t) => (
                    <li key={t} className="flex items-start text-gray-400">
                      <span className="text-white/40 mr-3 mt-0.5">—</span>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 07 · FAQ */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <EditorialHeading
              kicker="Sección 07 · FAQ"
              eyebrow="Respuestas directas"
              title={
                <>
                  Preguntas frecuentes sobre SEO local en <span className="text-gradient">{city}</span>
                </>
              }
            />
            <div className="mt-10 max-w-4xl space-y-4">
              {faqs.map((faq) => (
                <details
                  key={faq.question}
                  className="group rounded-2xl border border-white/10 bg-seo-card p-6 open:border-premium-gold/30 transition-colors"
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                    <h3 className="text-lg font-semibold">{faq.question}</h3>
                    <span className="text-premium-gold text-2xl leading-none flex-shrink-0 group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 text-gray-400 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* 08 · Enlaces relacionados */}
        <section className="py-16 md:py-20 bg-seo-card/30">
          <div className="container mx-auto px-4 md:px-6">
            <EditorialHeading
              kicker="Sección 08 · Enlaces"
              eyebrow="Siguiente paso"
              title={<>Servicios relacionados</>}
            />
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "SEO Local con IA", path: "/servicios/seo-local-ia", text: "El servicio completo y su metodología." },
                { label: "GEO · SEO 360", path: "/servicios/geo-seo-360", text: "Visibilidad en ChatGPT, Gemini y AI Overviews." },
                { label: "Gestión de reseñas", path: "/servicios/gestion-resenas-ia", text: "Solicitud ética y gestión de opiniones reales." },
                { label: "Zonas exclusivas", path: "/zonas-exclusivas", text: `Comprueba si tu zona de ${city} sigue libre.` },
              ].map((r) => (
                <Link
                  key={r.path}
                  to={r.path}
                  className="rounded-2xl border border-white/10 bg-seo-card p-6 hover:border-premium-gold/40 transition-colors"
                >
                  <h3 className="font-semibold mb-1">{r.label}</h3>
                  <p className="text-sm text-gray-400">{r.text}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-seo-blue/20 to-seo-purple/20 p-12 rounded-2xl border border-premium-gold/25">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">¿Tu competencia ya nos ha llamado?</h2>
              <p className="text-xl text-gray-300 mb-4">
                Solo trabajamos con un cliente por sector y zona en {city}. Si tu plaza está libre, hablémoslo
                antes de que otro la ocupe.
              </p>
              <p className="text-sm text-gray-400 mb-8">
                Diagnóstico inicial sin compromiso · Respuesta en menos de 2h
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://wa.me/34910626684" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-seo-blue hover:bg-seo-blue/80 text-white">
                    Consultar disponibilidad en {city} →
                  </Button>
                </a>
                <Link to="/agenda-consultoria">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    Agendar llamada de consultoría
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

